import { z } from "zod";
import { baseEntitySchema } from "./base";
import { ProductType } from "./product";

export enum EventType {
  WEBFLOW_FORM_SUBMISSION = "webflow_form_submission",
  SECURE_QUOTE = "secure_quote",
  SELL_SUBMISSION_FORM = "sell_submission_form",
  BUY_SUBMISSION_FORM = "buy_submission_form",
  HUBSPOT_UPDATE = "hubspot_update",
}

export const baseEventDataSchema = z.object({
  type: z.nativeEnum(EventType),
  productId: z.string(),
  customerId: z.string(),
  hubspotDealId: z.string(),
  data: z.record(z.string(), z.unknown()).optional(),
});

export type BaseEventData = z.infer<typeof baseEventDataSchema>;

export const baseEventSchema = baseEntitySchema.merge(baseEventDataSchema);
export type BaseEvent = z.infer<typeof baseEventSchema>;

export const webflowFormSubmissionEventDataSchema = baseEventDataSchema.extend({
  data: z.object({
    amount: z.number().min(1),
    buyOrSell: z.nativeEnum(ProductType),
  }),
});
export type WebflowFormSubmissionEventData = z.infer<
  typeof webflowFormSubmissionEventDataSchema
>;

export const webflowFormSubmissionEventSchema = baseEventSchema.merge(
  webflowFormSubmissionEventDataSchema,
);
export type WebflowFormSubmissionEvent = z.infer<
  typeof webflowFormSubmissionEventSchema
>;

export const secureQuoteFormSubmissionEventDataSchema =
  baseEventDataSchema.extend({
    data: z.object({
      miles: z.number().min(1),
      type: z.enum([ProductType.BUY, ProductType.SELL]),
      averageRate: z.number(),
      totalPayout: z.number(),
    }),
  });
export type SecureQuoteFormSubmissionEventData = z.infer<
  typeof secureQuoteFormSubmissionEventDataSchema
>;
export const secureQuoteFormSubmissionEventSchema = baseEventSchema.merge(
  secureQuoteFormSubmissionEventDataSchema,
);
export type SecureQuoteFormSubmissionEvent = z.infer<
  typeof secureQuoteFormSubmissionEventSchema
>;

export enum PaymentMethod {
  PAYPAL = "paypal",
  ECHECK = "echeck",
  WIRE = "wire",
}

export enum MilesExpirationDate {
  THREE_MONTHS = "THREE_MONTHS",
  SIX_MONTHS = "SIX_MONTHS",
  ONE_YEAR = "ONE_YEAR",
  NEVER = "NEVER",
}

export const sellSubmissionFormEventDataSchema = baseEventDataSchema.extend({
  data: z.object({
    frequentFlyerNumber: z.string().min(1, "Username is required"),
    accountEmail: z.string().email("Please enter a valid email address"),
    accountPassword: z
      .string()
      .min(4, "Password is required and must be at least 4 characters"),
    accountPhoneNumber: z.string().min(1, "Phone number is required"),
    milesExpirationDate: z.string().min(1, "Miles expiration is required"),
    paymentMethod: z.nativeEnum(PaymentMethod),
    paypalEmail: z.string().email().optional(),
    miles: z.number().min(1),
    averageRate: z.number(),
    totalPayout: z.number(),
    notes: z.string().optional(),
  }),
});
export type SellSubmissionFormEventData = z.infer<
  typeof sellSubmissionFormEventDataSchema
>;
export const sellSubmissionFormEventSchema = baseEventSchema.merge(
  sellSubmissionFormEventDataSchema,
);
export type SellSubmissionFormEvent = z.infer<
  typeof sellSubmissionFormEventSchema
>;

export const buySubmissionFormEventDataSchema = baseEventDataSchema.extend({
  data: z.object({
    miles: z.number().min(1),
    averageRate: z.number(),
    totalPayout: z.number(),
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    frequentFlyerNumber: z.string().min(1, "Username is required"),
    accountEmail: z.string().email("Please enter a valid email address"),
    receivedMilesBefore: z.string().min(1, "Received miles before is required"),
    notes: z.string().optional(),
  }),
});
export type BuySubmissionFormEventData = z.infer<
  typeof buySubmissionFormEventDataSchema
>;

export const buySubmissionFormEventSchema = baseEventSchema.merge(
  buySubmissionFormEventDataSchema,
);
export type BuySubmissionFormEvent = z.infer<
  typeof buySubmissionFormEventSchema
>;

export const hubspotUpdateEventDataSchema = baseEventDataSchema.extend({
  data: z.object({
    fieldName: z.string().min(1, "Field name is required"),
    oldValue: z.unknown(),
    newValue: z.unknown(),
  }),
});
export type HubspotUpdateEventData = z.infer<
  typeof hubspotUpdateEventDataSchema
>;
export const hubspotUpdateEventSchema = baseEventSchema.merge(
  hubspotUpdateEventDataSchema,
);
export type HubspotUpdateEvent = z.infer<typeof hubspotUpdateEventSchema>;

export type EventData =
  | WebflowFormSubmissionEventData
  | SecureQuoteFormSubmissionEventData
  | SellSubmissionFormEventData
  | BuySubmissionFormEventData
  | HubspotUpdateEventData;

export type Event =
  | WebflowFormSubmissionEvent
  | SecureQuoteFormSubmissionEvent
  | SellSubmissionFormEvent
  | BuySubmissionFormEvent
  | HubspotUpdateEvent;
